import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ExportOutlined, InfoCircleOutlined, PlayCircleOutlined, SafetyCertificateOutlined, StopOutlined } from '@ant-design/icons';

import { Badge, Button, Dialog, Empty, Spinner } from '../../../../components/ui';
import { useDuongDan } from '../../../../hooks/useDuongDan';
import { useLoi } from '../../../../hooks/useLoi';
import { useT } from '../../../../i18n';
import { KHONG_CO, ngayGioVN } from '../../../../lib/dinh-dang';
import { useToast } from '../../../../lib/toast';
import { HopXacNhanThaoTac } from '../../../nguoi-dung/components/HopXacNhanThaoTac';
import { TomTatMarkdown } from '../../../xu-ly-van-ban/components/tom-tat/TomTatMarkdown';
import { TruongThongTin } from '../lich-su/nhan';
import { LOAI_HANH_DONG, THUC_THI_HIEN, TRANG_THAI_TASK } from '../../constants';
import { useHuyTaskAgent, useThucThiHanhDong } from '../../hooks/useAgent';
import { MAN_CUA_HANH_DONG, nutThucThi, thucThiDuoc } from '../../nghiep-vu/agent';
import type { HanhDongAgent, KetQuaKiemRobot, TaskAgent } from '../../types';
import { HopThucThi } from './HopThucThi';
import { useThoiLuong } from './thoi-luong';

/**
 * Chi tiết task agent (`AgentTaskDetailDialog` của native): yêu cầu, báo cáo Markdown của agent và các hành động đề
 * xuất. Báo cáo dựng thành phần React (không `innerHTML`), chữ agent sinh không chạy được mã. Task lấy từ danh sách
 * đang hỏi lại, nên trạng thái tự cập nhật khi agent xử lý xong.
 */
export function HopChiTietTask({
  task,
  huyDuoc,
  chayDuoc,
  onClose,
}: {
  task: TaskAgent;
  huyDuoc: boolean;
  chayDuoc: boolean;
  onClose: () => void;
}) {
  const { t, locale } = useT();
  const thoiLuong = useThoiLuong();
  const [hoiHuy, setHoiHuy] = useState(false);
  const tt = TRANG_THAI_TASK[task.status];

  return (
    <Dialog
      open
      onClose={onClose}
      width={860}
      destroyOnHidden
      title={t('rpaAgent.chiTiet.tieuDe')}
      footer={
        <div className="flex justify-end">
          <Button variant="outline" size="md" onClick={onClose}>
            {t('chung.dong')}
          </Button>
        </div>
      }
    >
      <dl className="mb-4 grid grid-cols-1 gap-3 pt-2 sm:grid-cols-2 lg:grid-cols-4">
        <TruongThongTin nhan={t('rpaAgent.chiTiet.trangThai')}>
          <Badge variant={tt.variant}>{t(tt.khoa)}</Badge>
        </TruongThongTin>
        <TruongThongTin nhan={t('rpaAgent.chiTiet.agent')}>{task.model}</TruongThongTin>
        <TruongThongTin nhan={t('rpaAgent.chiTiet.phamVi')}>{t('rpaAgent.phamViNgay', { so: task.scopeDays })}</TruongThongTin>
        <TruongThongTin nhan={t('rpaAgent.chiTiet.giaoLuc')}>{ngayGioVN(task.createdAt, locale)}</TruongThongTin>
        {task.latencyMs !== null && (
          <TruongThongTin nhan={t('rpaAgent.chiTiet.thoiLuong')}>{thoiLuong(task.latencyMs)}</TruongThongTin>
        )}
      </dl>

      <section aria-labelledby={`yeu-cau-${task.id}`} className="mb-4">
        <h3 id={`yeu-cau-${task.id}`} className="mb-1 text-xs font-semibold text-content-muted">
          {t('rpaAgent.chiTiet.yeuCau')}
        </h3>
        <p className="whitespace-pre-wrap break-words rounded-lg bg-subtle p-3 text-sm text-content-primary">{task.prompt}</p>
      </section>

      {task.status === 'running' ? (
        <div className="flex flex-wrap items-center gap-2 py-6 text-sm text-content-secondary" role="status">
          <Spinner />
          {t('rpaAgent.chiTiet.dangXuLy')}
          {task.elapsedMs !== null && (
            <span className="tabular-nums text-content-muted">{t('rpaAgent.ds.daCho', { tg: thoiLuong(task.elapsedMs) })}</span>
          )}
          {huyDuoc && (
            <Button variant="outline" size="sm" className="ml-auto" icon={<StopOutlined />} onClick={() => setHoiHuy(true)}>
              {t('rpaAgent.huy.nut')}
            </Button>
          )}
        </div>
      ) : task.status === 'failed' ? (
        <Empty icon={InfoCircleOutlined} title={t('rpaAgent.chiTiet.loi')} description={task.error?.message || undefined} />
      ) : (
        <>
          <section aria-labelledby={`bao-cao-${task.id}`} className="mb-4">
            <h3 id={`bao-cao-${task.id}`} className="mb-1 text-xs font-semibold text-content-muted">
              {t('rpaAgent.chiTiet.baoCao')}
            </h3>
            <div className="max-h-[50vh] overflow-y-auto rounded-lg border border-default p-4">
              <TomTatMarkdown van={task.summary ?? ''} />
            </div>
          </section>
          <section aria-labelledby={`de-xuat-${task.id}`}>
            <h3 id={`de-xuat-${task.id}`} className="mb-1 text-xs font-semibold text-content-muted">
              {t('rpaAgent.chiTiet.deXuat', { so: task.actions.length })}
            </h3>
            {task.actions.length === 0 ? (
              <p className="text-sm text-content-secondary">{t('rpaAgent.chiTiet.khongDeXuat')}</p>
            ) : (
              <>
                <ul className="space-y-2">
                  {task.actions.map((a, i) => (
                    <MucHanhDong key={`${a.type}-${i}`} task={task} index={i} hanhDong={a} chayDuoc={chayDuoc} />
                  ))}
                </ul>
                <p className="mt-2 text-xs text-content-muted">{t('rpaAgent.chiTiet.ghiChu')}</p>
              </>
            )}
          </section>
        </>
      )}
      {hoiHuy && <HopHuyTask taskId={task.id} onClose={() => setHoiHuy(false)} />}
    </Dialog>
  );
}

/** Một hành động đề xuất (`AgentActionItem` của native): loại, đích, lý do, nút thực thi, kết quả lần thực thi. */
function MucHanhDong({ task, index, hanhDong, chayDuoc }: { task: TaskAgent; index: number; hanhDong: HanhDongAgent; chayDuoc: boolean }) {
  const { t } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const duongDan = useDuongDan();
  const thucThi = useThucThiHanhDong();
  const [xacNhan, setXacNhan] = useState(false);
  const loai = LOAI_HANH_DONG[hanhDong.type];
  const kiemRobot = hanhDong.type === 'check_robot';
  const nut = nutThucThi(hanhDong);

  const bam = () => {
    if (!kiemRobot) {
      setXacNhan(true);
      return;
    }
    // Kiểm tra Robot chỉ đọc: chạy thẳng, không cần hộp xác nhận.
    thucThi.mutate(
      { taskId: task.id, index },
      { onSuccess: () => toast.success(t('rpaAgent.thucThi.daKiem')), onError: (e) => toast.error(dichLoi(e)) },
    );
  };

  return (
    <li className="rounded-lg border border-default p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={loai.variant}>{t(loai.khoa)}</Badge>
        <span className="min-w-0 break-all font-mono text-xs text-content-primary">{hanhDong.target}</span>
        <div className="ml-auto flex items-center gap-3">
          <Link
            to={duongDan(MAN_CUA_HANH_DONG[hanhDong.type])}
            className="inline-flex items-center gap-1 text-xs text-primary-700 hover:underline"
          >
            {t('rpaAgent.chiTiet.moMan')}
            <ExportOutlined aria-hidden />
          </Link>
          {chayDuoc && thucThiDuoc(task, hanhDong) && (
            <Button
              size="sm"
              variant={kiemRobot ? 'outline' : 'primary'}
              icon={kiemRobot ? <SafetyCertificateOutlined /> : <PlayCircleOutlined />}
              loading={kiemRobot && thucThi.isPending}
              onClick={bam}
            >
              {t(`rpaAgent.thucThi.${nut}`)}
            </Button>
          )}
        </div>
      </div>
      {hanhDong.reason && <p className="mt-1.5 text-sm text-content-secondary">{hanhDong.reason}</p>}
      <DongThucThi hanhDong={hanhDong} />
      {xacNhan && <HopThucThi taskId={task.id} index={index} hanhDong={hanhDong} onClose={() => setXacNhan(false)} />}
    </li>
  );
}

function DongThucThi({ hanhDong }: { hanhDong: HanhDongAgent }) {
  const { t, locale } = useT();
  const duongDan = useDuongDan();
  const tt = hanhDong.execution;
  if (!tt) return null;
  if (tt.status === 'running') {
    return (
      <p className="mt-1.5 flex items-center gap-2 text-sm text-content-secondary" role="status">
        <Spinner />
        {t('rpaAgent.thucThi.dangChay')}
      </p>
    );
  }
  const kq = THUC_THI_HIEN[tt.status];
  if (tt.status === 'failed') {
    return (
      <p className="mt-1.5 flex flex-wrap items-center gap-2 text-sm text-danger-700" role="alert">
        <Badge variant={kq.variant}>{t(kq.khoa)}</Badge>
        <span className="min-w-0 break-words">{tt.error?.message}</span>
      </p>
    );
  }
  return (
    <div className="mt-1.5 text-sm text-content-secondary">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={kq.variant}>{t(kq.khoa)}</Badge>
        <span>{t('rpaAgent.thucThi.luc', { luc: ngayGioVN(tt.finishedAt, locale) })}</span>
        {tt.runId && (
          <Link to={duongDan('/rpa/history')} className="inline-flex items-center gap-1 text-xs text-primary-700 hover:underline">
            {t('rpaAgent.thucThi.xemLichSu')}
            <ExportOutlined aria-hidden />
          </Link>
        )}
      </div>
      {tt.result && <DongKiemRobot kq={tt.result} />}
    </div>
  );
}

function DongKiemRobot({ kq }: { kq: KetQuaKiemRobot }) {
  const { t } = useT();
  const tk = kq.enabled === null ? 'rpaAgent.thucThi.tkKhongRo' : kq.enabled ? 'rpaAgent.thucThi.tkBat' : 'rpaAgent.thucThi.tkKhoa';
  return (
    <p className="mt-1.5">
      {t('rpaAgent.thucThi.robotDong', { ten: kq.name, may: kq.machineName || KHONG_CO, tt: kq.status })} · {t(tk)}
    </p>
  );
}

export function HopHuyTask({ taskId, onClose }: { taskId: string; onClose: () => void }) {
  const { t } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const huy = useHuyTaskAgent();
  return (
    <HopXacNhanThaoTac
      open
      nguyHiem
      tieuDe={t('rpaAgent.huy.tieuDe')}
      than={t('rpaAgent.huy.than')}
      nhanXacNhan={t('rpaAgent.huy.nut')}
      dangChay={huy.isPending}
      loi={huy.error ? dichLoi(huy.error) : null}
      onClose={onClose}
      onXacNhan={() =>
        huy.mutate(taskId, {
          onSuccess: () => {
            toast.success(t('rpaAgent.huy.daHuy'));
            onClose();
          },
        })
      }
    />
  );
}
